import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useMemo, useCallback } from 'react';
import { INCOME_SEARCH_PARAMS } from '../constants';
import { useWarehouseIncomesData } from './hooks/useWarehouseIncomesData';
import { Close, FilterAltOff } from '@mui/icons-material';

export const Search = () => {
  const {
    params: {
      types,
      warehouses,
      suppliers,
      selectedType,
      selectedWarehouse,
      selectedSupplier,
      dateFrom,
      dateTo,
    },
    actions: {
      setType,
      setWarehouse,
      setSupplier,
      setDateFrom,
      setDateTo,
      clearAll,
    },
  } = useSearch();

  const hasAnyFilter = Boolean(
    selectedType || selectedWarehouse || selectedSupplier || dateFrom || dateTo
  );
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Stack
      gap={1}
      p={1}
      sx={{
        flexDirection: { md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
      }}
    >
      <Box flex={1}>
        <Autocomplete
          options={types ?? []}
          value={selectedType}
          onChange={(_, newValue) =>
            setType(newValue ? String(newValue.id) : '')
          }
          getOptionLabel={(option) => option.description}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Тип"
              size="small"
              sx={{ backgroundColor: (theme) => theme.palette.light.lighter }}
            />
          )}
          componentsProps={{
            clearIndicator: {
              title: 'Сбросить',
              sx: {
                display: selectedType ? 'inline-flex' : 'none',
              },
            },
          }}
          fullWidth
          noOptionsText="Не найдено"
        />
      </Box>

      <Box flex={1}>
        <Autocomplete
          options={warehouses ?? []}
          value={selectedWarehouse}
          onChange={(_, newValue) => setWarehouse(newValue?.id ?? '')}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Склад"
              size="small"
              sx={{ backgroundColor: (theme) => theme.palette.light.lighter }}
            />
          )}
          componentsProps={{
            clearIndicator: {
              title: 'Сбросить',
              sx: {
                display: selectedWarehouse ? 'inline-flex' : 'none',
              },
            },
          }}
          fullWidth
          noOptionsText="Не найдено"
        />
      </Box>

      <Box flex={1}>
        <Autocomplete
          options={suppliers ?? []}
          value={selectedSupplier}
          onChange={(_, newValue) => setSupplier(newValue?.id ?? '')}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Поставщик"
              size="small"
              sx={{ backgroundColor: (theme) => theme.palette.light.lighter }}
            />
          )}
          componentsProps={{
            clearIndicator: {
              title: 'Сбросить',
              sx: {
                display: selectedSupplier ? 'inline-flex' : 'none',
              },
            },
          }}
          fullWidth
          noOptionsText="Не найдено"
        />
      </Box>
      <Divider
        orientation={isDesktop ? 'vertical' : 'horizontal'}
        sx={{ visibility: 'hidden' }}
      />
      <Box display="flex" alignItems="baseline" gap={1} flex="1">
        <DateTimePicker
          ampm={false}
          inputFormat="dd.MM.yy HH:mm"
          value={dateFrom ? new Date(dateFrom) : null}
          onChange={(newValue) =>
            setDateFrom(newValue ? newValue.toISOString() : '')
          }
          renderInput={(props) => (
            <TextField
              {...props}
              id="dateFrom"
              name="dateFrom"
              label="Дата от"
              size="small"
              sx={{
                minWidth: '168px',
                backgroundColor: (theme) => theme.palette.light.lighter,
              }}
              InputProps={{
                ...props.InputProps,
                endAdornment: (
                  <>
                    {dateFrom ? (
                      <InputAdornment
                        position="end"
                        style={{ marginRight: -8 }}
                      >
                        <IconButton
                          size="small"
                          title="Сбросить"
                          onClick={() => setDateFrom('')}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      props.InputProps?.endAdornment
                    )}
                  </>
                ),
              }}
            />
          )}
        />
        <DateTimePicker
          ampm={false}
          inputFormat="dd.MM.yy HH:mm"
          value={dateTo ? new Date(dateTo) : null}
          onChange={(newValue) =>
            setDateTo(newValue ? newValue.toISOString() : '')
          }
          renderInput={(props) => (
            <TextField
              {...props}
              id="dateTo"
              name="dateTo"
              label="Дата по"
              size="small"
              InputProps={{
                ...props.InputProps,
                endAdornment: (
                  <>
                    {dateTo ? (
                      <InputAdornment
                        position="end"
                        style={{ marginRight: -8 }}
                      >
                        <IconButton
                          size="small"
                          title="Сбросить"
                          onClick={() => setDateTo('')}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      props.InputProps?.endAdornment
                    )}
                  </>
                ),
              }}
              sx={{
                minWidth: '168px',
                backgroundColor: (theme) => theme.palette.light.lighter,
              }}
            />
          )}
        />
      </Box>
      {isDesktop ? (
        <Tooltip title={hasAnyFilter ? 'Очистить все фильтры' : null}>
          <span>
            <IconButton
              size="small"
              onClick={clearAll}
              disabled={!hasAnyFilter}
            >
              <FilterAltOff fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant="text"
          onClick={clearAll}
          startIcon={<FilterAltOff />}
          sx={{ width: '100%' }}
        >
          Очистить фильтры
        </Button>
      )}
    </Stack>
  );
};

export const useSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { types, warehouses, suppliers } = useWarehouseIncomesData();

  const typeId = searchParams.get(INCOME_SEARCH_PARAMS.TYPE) ?? '';
  const warehouseId = searchParams.get(INCOME_SEARCH_PARAMS.WAREHOUSE_ID) ?? '';
  const supplierId = searchParams.get(INCOME_SEARCH_PARAMS.SUPPLIER_ID) ?? '';
  const dateFrom = searchParams.get(INCOME_SEARCH_PARAMS.DATE_FROM) ?? '';
  const dateTo = searchParams.get(INCOME_SEARCH_PARAMS.DATE_TO) ?? '';
  const status = searchParams.get(INCOME_SEARCH_PARAMS.STATUS) ?? '';

  const setParam = useCallback(
    (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams);
      if (value) newParams.set(key, value);
      else newParams.delete(key);
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const setType = useCallback(
    (value: string) => setParam(INCOME_SEARCH_PARAMS.TYPE, value),
    [setParam]
  );
  const setWarehouse = useCallback(
    (value: string) => setParam(INCOME_SEARCH_PARAMS.WAREHOUSE_ID, value),
    [setParam]
  );
  const setSupplier = useCallback(
    (value: string) => setParam(INCOME_SEARCH_PARAMS.SUPPLIER_ID, value),
    [setParam]
  );
  const setDateFrom = useCallback(
    (value: string) => setParam(INCOME_SEARCH_PARAMS.DATE_FROM, value),
    [setParam]
  );
  const setDateTo = useCallback(
    (value: string) => setParam(INCOME_SEARCH_PARAMS.DATE_TO, value),
    [setParam]
  );

  const clearAll = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    Object.values(INCOME_SEARCH_PARAMS).forEach((key) => newParams.delete(key));
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const selectedType = useMemo(
    () => types?.find((t) => String(t.id) === typeId) ?? null,
    [types, typeId]
  );
  const selectedWarehouse = useMemo(
    () => warehouses?.find((w) => w.id === warehouseId) ?? null,
    [warehouses, warehouseId]
  );
  const selectedSupplier = useMemo(
    () => suppliers?.find((s) => s.id === supplierId) ?? null,
    [suppliers, supplierId]
  );

  const params = {
    type: typeId,
    warehouseId,
    supplierId,
    status,
    dateFrom,
    dateTo,
    types,
    warehouses,
    suppliers,
    selectedType,
    selectedWarehouse,
    selectedSupplier,
  };

  const actions = useMemo(
    () => ({
      setType,
      setWarehouse,
      setSupplier,
      setDateFrom,
      setDateTo,
      clearAll,
    }),
    [setType, setWarehouse, setSupplier, setDateFrom, setDateTo, clearAll]
  );

  return { params, actions };
};
